import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA, trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { DWI_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * The plan qualifier — the most important non-negotiable on this page.
 *
 * Every capability the page describes is Heirloom-only, and not partially:
 * GET /api/events/:eventId/public strips the whole invitation payload for any
 * plan other than `pro` (api/routes/events.routes.mjs), which then fails the
 * invite app's readiness gate, so a Blossom or Bouquet couple's invitation URL
 * renders "Coming soon" — no invitation, no preview, nothing. Every route
 * behind it (RSVP, guest list, email invitations, seating, planning) is mounted
 * behind `requireProPlanWithCode`.
 *
 * So this section says three things in order, and a reader who takes only one
 * of them still cannot come away with the wrong idea:
 *
 *   1. the invitation is included with Heirloom,
 *   2. photo sharing is on every plan and the invitation is NOT,
 *   3. features vary by plan — with a link to the plans.
 *
 * Named as a section (`heading-136`, its own impression) rather than a footnote
 * on the end of another one, because a plan qualifier that reads as fine print
 * is a plan qualifier someone can honestly say they missed.
 */
const InvitationPlan = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.PLAN, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">
              {t("digitalWeddingInvitation.plan.heading")}
            </h2>
            <p className="body-20">
              {t("digitalWeddingInvitation.plan.body1")}
            </p>
            <p className="body-20">
              {t("digitalWeddingInvitation.plan.body2")}
            </p>
          </div>
        </div>

        <p className="wps-plan-note">
          {t("digitalWeddingInvitation.plan.note")}{" "}
          <a className="wps-text-link" href={localize("/#pricing")} onClick={scrollToPlans}>
            {t("digitalWeddingInvitation.plan.noteLink")}
          </a>
        </p>

        {/*
          Hand-off to the sibling product pages. `wpl-cross-links` is the
          shared row treatment /wedding-planning introduced for exactly this
          job — two or more contextual links under the plan qualifier — so the
          second link arrives without a second layout. The RSVP link is
          contextual rather than decorative: this page describes the invitation
          guests open, and /wedding-rsvp describes the reply they give inside
          it.
        */}
        <div className="wpl-cross-links">
          <a
            className="wps-text-link"
            href={localize("/wedding-rsvp")}
            onClick={() =>
              trackCTA("digital_wedding_invitation_plan", {
                ...analyticsContext,
                ctaName: "wedding_rsvp",
              })
            }
          >
            {t("digitalWeddingInvitation.plan.rsvpLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-planning")}
            onClick={() =>
              trackCTA("digital_wedding_invitation_plan", {
                ...analyticsContext,
                ctaName: "wedding_planning",
              })
            }
          >
            {t("digitalWeddingInvitation.plan.planningLink")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default InvitationPlan;
